import React from "react";
import { Formik, Form, FastField } from "formik";
import { Grid, MenuItem, TextField } from "@material-ui/core";
import { KeyboardDatePicker } from "@material-ui/pickers";

import {
  ButtonsWrapper,
  CancelButton,
  SaveButton
} from "../../../../elements/Button";
import {
  FullWidthTextField,
  FullWidthSelect,
  NumberField
} from "../../../../elements/FormField";
import Section from "../../../../elements/Section";
import SectionHeader from "../../../../elements/SectionHeader";
import I18nText from "../../../../elements/I18nText";
import { createFieldErrorMessage } from "../../../../utils/formErrors";
import { numberValidator } from "../../../../utils/formValidators";
import {
  MAINTENANCE_PRIORITY,
  MAINTENANCE_STATUS
} from "../../../../domain/maintenance";

const INTERVAL_PRESETS = [90, 180, 365];
const CUSTOM_INTERVAL = "custom";

const validateTitle = value =>
  value ? undefined : createFieldErrorMessage("default", "required");

const MaintenanceForm = ({ values, isEdit = false, onClose, onSubmit }) => {
  return (
    <Formik
      initialValues={values}
      onSubmit={(values, { setFieldError, setSubmitting }) =>
        onSubmit(values, { setFieldError, setSubmitting })
      }
    >
      {({ values, setFieldValue, submitForm, isSubmitting }) => {
        const intervalPreset = INTERVAL_PRESETS.includes(
          values.maintenanceInterval
        )
          ? values.maintenanceInterval
          : CUSTOM_INTERVAL;

        return (
          <Form>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Section>
                  <SectionHeader>
                    <I18nText>devices.maintenance.sections.details</I18nText>
                  </SectionHeader>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <FullWidthTextField
                        name="title"
                        variant="outlined"
                        required
                        validate={validateTitle}
                        label={
                          <I18nText>devices.maintenance.labels.title</I18nText>
                        }
                        inputProps={{ maxLength: 96, autoComplete: "off" }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <FullWidthTextField
                        name="description"
                        variant="outlined"
                        multiline
                        label={
                          <I18nText>
                            devices.maintenance.labels.description
                          </I18nText>
                        }
                        inputProps={{
                          minRows: 3,
                          maxLength: 256,
                          autoComplete: "off"
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={isEdit ? 4 : 6}>
                      <FullWidthSelect
                        name="priority"
                        label={
                          <I18nText>
                            devices.maintenance.labels.priority
                          </I18nText>
                        }
                      >
                        {Object.values(MAINTENANCE_PRIORITY).map(priority => (
                          <MenuItem key={priority} value={priority}>
                            <I18nText>{`devices.maintenance.priorities.${priority}`}</I18nText>
                          </MenuItem>
                        ))}
                      </FullWidthSelect>
                    </Grid>
                    <Grid item xs={12} sm={isEdit ? 4 : 6}>
                      <FullWidthTextField
                        name="technician"
                        variant="outlined"
                        label={
                          <I18nText>
                            devices.maintenance.labels.technician
                          </I18nText>
                        }
                        inputProps={{ maxLength: 96, autoComplete: "off" }}
                      />
                    </Grid>
                    {isEdit && (
                      <Grid item xs={12} sm={4}>
                        <FullWidthSelect
                          name="status"
                          label={
                            <I18nText>
                              devices.maintenance.labels.status
                            </I18nText>
                          }
                        >
                          {Object.values(MAINTENANCE_STATUS).map(status => (
                            <MenuItem key={status} value={status}>
                              <I18nText>{`devices.maintenance.statuses.${status}`}</I18nText>
                            </MenuItem>
                          ))}
                        </FullWidthSelect>
                      </Grid>
                    )}
                  </Grid>
                </Section>
              </Grid>

              <Grid item xs={12}>
                <Section>
                  <SectionHeader>
                    <I18nText>devices.maintenance.sections.schedule</I18nText>
                  </SectionHeader>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={4}>
                      <KeyboardDatePicker
                        fullWidth
                        size="small"
                        variant="dialog"
                        inputVariant="outlined"
                        label={
                          <I18nText>
                            devices.maintenance.labels.startDate
                          </I18nText>
                        }
                        value={values.maintenanceAt}
                        onChange={value =>
                          setFieldValue("maintenanceAt", value)
                        }
                        format="dd.MM.yyyy"
                      />
                    </Grid>
                    <Grid
                      item
                      xs={12}
                      sm={intervalPreset === CUSTOM_INTERVAL ? 4 : 8}
                    >
                      <TextField
                        select
                        fullWidth
                        size="small"
                        variant="outlined"
                        label={
                          <I18nText>
                            devices.maintenance.labels.interval
                          </I18nText>
                        }
                        value={intervalPreset}
                        onChange={e => {
                          const value = e.target.value;
                          if (value === CUSTOM_INTERVAL) {
                            setFieldValue("maintenanceInterval", "");
                          } else {
                            setFieldValue("maintenanceInterval", value);
                          }
                        }}
                      >
                        {INTERVAL_PRESETS.map(preset => (
                          <MenuItem key={preset} value={preset}>
                            <I18nText values={{ days: preset }}>
                              devices.maintenance.common.intervalDaysValue
                            </I18nText>
                          </MenuItem>
                        ))}
                        <MenuItem value={CUSTOM_INTERVAL}>
                          <I18nText>
                            devices.maintenance.labels.intervalCustom
                          </I18nText>
                        </MenuItem>
                      </TextField>
                    </Grid>
                    {intervalPreset === CUSTOM_INTERVAL && (
                      <Grid item xs={12} sm={4}>
                        <FastField
                          component={NumberField}
                          name="maintenanceInterval"
                          validate={numberValidator({ required: true, min: 1 })}
                          required
                          min={1}
                          label="devices.maintenance.labels.intervalDaysCustom"
                        />
                      </Grid>
                    )}
                  </Grid>
                </Section>
              </Grid>

              <Grid item xs={12}>
                <ButtonsWrapper
                  leftButton={
                    <CancelButton onClick={onClose} disabled={isSubmitting} />
                  }
                  rightButton={
                    <SaveButton onClick={submitForm} disabled={isSubmitting} />
                  }
                />
              </Grid>
            </Grid>
          </Form>
        );
      }}
    </Formik>
  );
};

export default MaintenanceForm;
